import { useQueryClient } from '@tanstack/react-query';
import orderBy from 'lodash-es/orderBy';
import moment from 'moment';
import { useMemo } from 'react';
import { convertArrayToObject } from '../../../../helper/objectHelpers';
import {
  useGetETSTicketAttachmentsQuery,
  useGetETSTicketCommentsQuery,
  useGetETSTicketInformation,
  useGetETSTicketNotificationsQuery,
  useGetETSTicketPartsQuery,
  useGetETSTicketQuery,
  useGetETSTicketRatingQuery,
} from './tickets-api';
import {
  type FetchETSTicketInformationResponseData,
  type FetchETSTicketResponseData,
  type FetchETSTicketsResponseData,
  TicketsClientQueryKeys,
} from './tickets-api-helpers';

interface UseGetETSTicketSelectorProps {
  ticketId: string | undefined;
}

export const useGetETSTicketSelector = ({ ticketId }: UseGetETSTicketSelectorProps) => {
  const { data: ticketData } = useGetETSTicketQuery({ path: { ticket_id: ticketId || '' } });
  const queryClient = useQueryClient();

  if (!ticketId) return undefined;

  const queries = queryClient.getQueriesData({
    queryKey: [TicketsClientQueryKeys.GET_ETS_TICKETS],
  });

  const ticketFromList = queries
    .flatMap((query) => {
      const queryData = query[1] as FetchETSTicketsResponseData;
      const tickets = queryData ? queryData.data : ([] as FetchETSTicketsResponseData['data']);
      return tickets;
    })
    .find((curTicket) => curTicket.id === ticketId);

  return ticketData || ticketFromList;
};

export const useGetETSTicketNotificationSelector = (ticketId: string | undefined) => {
  const { data } = useGetETSTicketNotificationsQuery();

  if (!ticketId || !data) return undefined;

  return data.notifications.find((curNotification) => curNotification.ticketId === ticketId);
};

export enum TicketMessageType {
  COMMENT = 'comment',
  BOT_MESSAGE = 'botMessage',
  MEDIA = 'media',
  NEW_MESSAGE_INDICATOR = 'newMessagesIndicator',
  PART = 'part',
  RATING = 'rating',
}

export type CommentMessage = {
  type: TicketMessageType.COMMENT;
} & FetchETSTicketInformationResponseData['comments'][number];

export type BotCommentMessage = {
  type: TicketMessageType.BOT_MESSAGE;
} & FetchETSTicketInformationResponseData['comments'][number];

export type MediaMessage = {
  type: TicketMessageType.MEDIA;
} & FetchETSTicketInformationResponseData['attachments'][number];

export type NewMessagesIndicator = {
  type: TicketMessageType.NEW_MESSAGE_INDICATOR;
  createdAt: string;
  id: string;
};
export type PartMessage = {
  type: TicketMessageType.PART;
  parts?: FetchETSTicketInformationResponseData['parts']['parts'];
  alternatives?: FetchETSTicketInformationResponseData['parts']['alternatives'];
  defectives?: FetchETSTicketInformationResponseData['parts']['defectives'];
  description?:
    | FetchETSTicketInformationResponseData['parts']['parts'][number]['description']
    | FetchETSTicketInformationResponseData['parts']['alternatives'][number]['description'];
  manufacturer?:
    | FetchETSTicketInformationResponseData['parts']['parts'][number]['manufacturer']
    | FetchETSTicketInformationResponseData['parts']['alternatives'][number]['manufacturer'];
  kbn?:
    | FetchETSTicketInformationResponseData['parts']['parts'][number]['kbn']
    | FetchETSTicketInformationResponseData['parts']['alternatives'][number]['kbn'];
  externalJiraKey?: string;
};
export type RatingMessage = {
  type: TicketMessageType.RATING;
} & FetchETSTicketInformationResponseData['rating'];

export type TicketMessage =
  | CommentMessage
  | BotCommentMessage
  | MediaMessage
  | NewMessagesIndicator
  | PartMessage
  | RatingMessage;

interface UseGetETSTicketMessagesSelectorProps {
  ticketId: string;
  currentUserId: string;
}

interface TransformTicketMessagesParams {
  ticket?: FetchETSTicketResponseData | FetchETSTicketInformationResponseData;
  comments?: CommentMessage[];
  attachments?: MediaMessage[];
  parts?: PartMessage;
  rating?: RatingMessage;
  ticketNotification?: FetchETSTicketInformationResponseData['notification'];
  currentUserId: string;
}

export const transformTicketToMessages = ({
  ticket,
  comments = [],
  attachments = [],
  parts = { type: TicketMessageType.PART, parts: [], alternatives: [], defectives: [] },
  rating = { comment: '', rating: 0, type: TicketMessageType.RATING },
  ticketNotification,
  currentUserId,
}: TransformTicketMessagesParams): TicketMessage[] => {
  if (!ticket || !comments || !attachments || !parts) return [];

  const initialCommentMessage: CommentMessage = {
    type: TicketMessageType.COMMENT,
    createdAt: ticket.createdAt || '',
    authorId: currentUserId,
    html: ticket.description,
    authorImageUrl: '',
    authorName: '',
    fromBeem: false,
    id: '',
    isBot: false,
    updatedAt: undefined,
  };

  let result: TicketMessage[] = [initialCommentMessage];

  result.push(
    ...comments
      .filter((c) => !c.isBot)
      .map((comment) => ({
        ...comment,
        type: TicketMessageType.COMMENT as const,
      })),
  );

  result.push(
    ...comments
      .filter((c) => c.isBot)
      .map((comment) => ({
        ...comment,
        type: TicketMessageType.BOT_MESSAGE as const,
      })),
  );

  result.push(
    ...attachments.map((attachment) => ({
      ...attachment,
      type: TicketMessageType.MEDIA as const,
    })),
  );

  if (ticketNotification) {
    const unreadIndicatorDate = new Date(
      Math.max(
        new Date(ticketNotification.lastViewedAt).getTime(),
        ...result
          .filter(
            (x) =>
              ((x.type === TicketMessageType.BOT_MESSAGE || x.type === TicketMessageType.COMMENT) &&
                x.fromBeem &&
                !x.isBot) ||
              (x.type === TicketMessageType.MEDIA && x.fromBeem),
          )
          .map((x) => ('createdAt' in x && x.createdAt ? new Date(x.createdAt).getTime() : 0)),
      ),
    ).toISOString();

    result.push({
      type: TicketMessageType.NEW_MESSAGE_INDICATOR,
      createdAt: moment(unreadIndicatorDate).toISOString(),
      id: ticketNotification.id,
    });
  }

  result = orderBy(result, (x) => ('createdAt' in x && x.createdAt ? new Date(x.createdAt) : new Date()), 'asc');

  // Fallback in case something goes wrong and there are no messages after the new message indicator
  // Currently should not happen at all.
  const lastMessage = result[result.length - 1];
  if (lastMessage && lastMessage.type === TicketMessageType.NEW_MESSAGE_INDICATOR) {
    result.pop();
  }

  if (ticket.status === 'done') {
    result.push(
      ...[
        ...(parts.parts || []).map((part, index) => ({
          type: TicketMessageType.PART as const,
          partType: 'parts',
          index,
          externalJiraKey: ticket.externalJiraKey,
          ...part,
        })),
      ],
      ...[
        ...(parts.alternatives || []).map((part, index) => ({
          type: TicketMessageType.PART as const,
          partType: 'alternatives',
          index,
          externalJiraKey: ticket.externalJiraKey,
          ...part,
        })),
      ],
    );
    result.push({
      ...rating,
      type: TicketMessageType.RATING,
    });
  }

  return result;
};

export const useGetETSTicketMessagesSelector = ({ ticketId, currentUserId }: UseGetETSTicketMessagesSelectorProps) => {
  const ticket = useGetETSTicketSelector({ ticketId });
  const ticketNotification = useGetETSTicketNotificationSelector(ticketId);
  const { data: commentsData } = useGetETSTicketCommentsQuery({ path: { ticket_id: ticketId } });
  const { data: attachmentsData } = useGetETSTicketAttachmentsQuery({
    path: { ticket_id: ticketId },
  });
  const { data: parts } = useGetETSTicketPartsQuery({ path: { ticket_id: ticketId } });
  const { data: ratingData } = useGetETSTicketRatingQuery({ path: { ticket_id: ticketId } });

  return transformTicketToMessages({
    ticket,
    comments: commentsData?.comments?.map((comment) => ({ ...comment, type: TicketMessageType.COMMENT })),
    attachments: attachmentsData?.attachments?.map((attachment) => ({ ...attachment, type: TicketMessageType.MEDIA })),
    parts: parts ? { ...parts, type: TicketMessageType.PART } : undefined,
    rating: ratingData
      ? { ...ratingData, type: TicketMessageType.RATING }
      : { comment: '', rating: 0, type: TicketMessageType.RATING },
    ticketNotification,
    currentUserId,
  });
};

export const useGetETSTicketMessagesInformationSelector = ({
  ticketId,
  currentUserId,
}: { ticketId: string; currentUserId: string }) => {
  const { data: ticket, isPending } = useGetETSTicketInformation({ path: { ticket_id: ticketId } });

  const messages = useMemo(() => {
    return transformTicketToMessages({
      ticket,
      comments: ticket?.comments?.map((comment) => ({ ...comment, type: TicketMessageType.COMMENT })),
      attachments: ticket?.attachments?.map((attachment) => ({ ...attachment, type: TicketMessageType.MEDIA })),
      parts: ticket?.parts ? { ...ticket.parts, type: TicketMessageType.PART } : undefined,
      rating: ticket?.rating
        ? { ...ticket.rating, type: TicketMessageType.RATING }
        : { comment: '', rating: 0, type: TicketMessageType.RATING },
      ticketNotification: ticket?.notification,
      currentUserId,
    });
  }, [ticket, currentUserId]);

  return {
    messages,
    isPending,
    createdByUserId: ticket?.createdByUserId,
    creatorName: ticket?.creatorName,
  };
};

interface UseGetETSTicketMessagesLoadingSelectorProps {
  ticketId: string;
}

export const useGetETSTicketMessagesLoadingSelector = ({ ticketId }: UseGetETSTicketMessagesLoadingSelectorProps) => {
  const { isFetching: commentsLoading } = useGetETSTicketCommentsQuery({
    path: { ticket_id: ticketId },
  });
  const { isFetching: attachmentsLoading } = useGetETSTicketAttachmentsQuery({
    path: { ticket_id: ticketId },
  });
  const { isFetching: partsLoading } = useGetETSTicketPartsQuery({
    path: { ticket_id: ticketId },
  });
  const { isFetching: ticketLoading } = useGetETSTicketQuery({
    path: { ticket_id: ticketId },
  });
  const { isFetching: notificatonsLoading } = useGetETSTicketNotificationsQuery();

  return commentsLoading || attachmentsLoading || partsLoading || ticketLoading || notificatonsLoading;
};

export const useGetETSTicketNotificationsSelector = () => {
  const { data: ticketNotificationsData } = useGetETSTicketNotificationsQuery();
  const ticketNotifications = useMemo(
    () => (ticketNotificationsData ? convertArrayToObject(ticketNotificationsData.notifications, 'ticketId') : {}),
    [ticketNotificationsData],
  );
  return ticketNotifications;
};

export const useGetETSTicketNotificationsCountSelector = () => {
  const ticketNotifications = useGetETSTicketNotificationsSelector();
  const ticketNotificationsCount = useMemo(() => Object.keys(ticketNotifications).length, [ticketNotifications]);
  return ticketNotificationsCount;
};

export const useGetETSTicketNotificationsForChannelSelector = (selectedChannelId: string) => {
  const { data: ticketNotificationsData } = useGetETSTicketNotificationsQuery();
  if (!selectedChannelId) return [];
  const ticketNotifications = ticketNotificationsData ? ticketNotificationsData.notifications : [];
  return ticketNotifications.filter(({ channelId }) => selectedChannelId === channelId).map(({ id }) => id);
};

export const useGetETSTicketNotificationsCountForChannelSelector = (selectedChannelId: string) => {
  const ticketNotifications = useGetETSTicketNotificationsForChannelSelector(selectedChannelId);
  return ticketNotifications.length;
};
